import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Button } from './Button.tsx';
import './confirm-dialog.css';

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  body?: ReactNode;
  cancelLabel?: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Small confirmation modal for destructive table and row actions.
 *
 * The app still owns the mutation; this component only provides the accessible
 * confirmation surface and action layout.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  cancelLabel = 'Cancel',
  confirmLabel,
  confirmDisabled = false,
  danger = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const titleId = useId();
  const bodyId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    cancelRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="confirmDialogScrim" role="presentation" onMouseDown={onCancel}>
      <section
        className="confirmDialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(body ? { 'aria-describedby': bodyId } : {})}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="confirmDialogBody">
          <h2 id={titleId} className="confirmDialogTitle">
            {title}
          </h2>
          {body ? (
            <p id={bodyId} className="confirmDialogCopy">
              {body}
            </p>
          ) : null}
        </div>

        <footer className="confirmDialogActions">
          <Button ref={cancelRef} variant="quiet" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'danger' : 'confirm'}
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </footer>
      </section>
    </div>
  );
}
